import React, { useState, useEffect, useCallback } from "react";
import BackgroundAnimation from "../components/BackgroundAnimation";
import ChangePasswordModal from "../components/ChangePasswordModal";
import PositionsTable from "../components/PositionsTable";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { tokenManager, schwabAPI } from "../utils/apiClient";
import { useAuth } from "../hooks/useAuth";
import { Key, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";

function FundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, isAuthenticated: isUserAuthenticated, changePassword } = useAuth();
  const user = authUser || location.state?.user || tokenManager.getUser();
  const [activeTab, setActiveTab] = useState('fund');
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [schwabAuthenticated, setSchwabAuthenticated] = useState(false);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // If no user data is present and not authenticated, redirect to login
  if (!user && !isUserAuthenticated) {
    return <Navigate to="/ctshy-login" replace />;
  }

  // Check Schwab authentication status
  useEffect(() => {
    checkSchwabAuth();
    
    // Check for OAuth callback parameters
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === 'success') {
      toast.success('Successfully connected to Schwab!');
      setSchwabAuthenticated(true);
      fetchPositions();
      // Clean up URL
      window.history.replaceState({}, document.title, '/fund');
    } else if (params.get('error')) {
      toast.error('Failed to connect to Schwab: ' + params.get('error'));
      window.history.replaceState({}, document.title, '/fund');
    }
  }, [location]);

  // Check Schwab authentication
  const checkSchwabAuth = async () => {
    try {
      const { authenticated } = await schwabAPI.checkStatus();
      setSchwabAuthenticated(authenticated);
      if (authenticated) {
        await fetchPositions();
      }
    } catch (error) {
      console.error('Error checking Schwab auth:', error);
      // Don't throw error, just set as not authenticated
      setSchwabAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch positions from Schwab
  const fetchPositions = async () => {
    try {
      const data = await schwabAPI.getPositions();
      setPositions(data.positions || []);
      setLastUpdated(new Date(data.lastUpdated));
    } catch (error) {
      console.error('Error fetching positions:', error);
      if (error.error === 'Please authenticate with Schwab first') {
        setSchwabAuthenticated(false);
      }
    }
  };

  // Auto-refresh positions every 7 seconds
  useEffect(() => {
    if (!schwabAuthenticated || !autoRefresh) return;

    const interval = setInterval(() => {
      fetchPositions();
    }, 7000);

    return () => clearInterval(interval);
  }, [schwabAuthenticated, autoRefresh]);

  // Initiate Schwab OAuth
  const handleConnectSchwab = async () => {
    try {
      const { authUrl } = await schwabAPI.initiateAuth();
      window.location.href = authUrl;
    } catch (error) {
      toast.error('Failed to initiate Schwab authentication');
      console.error(error);
    }
  };

  // Manual refresh
  const handleManualRefresh = async () => {
    setLoading(true);
    await fetchPositions();
    setLoading(false);
    toast.success('Positions refreshed');
  };

  const handleChangePassword = () => {
    setIsChangePasswordModalOpen(true);
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
  };

  const handlePasswordChange = async (passwordData) => {
    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success("Password changed successfully!");
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.error || error.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <BackgroundAnimation />
      <div className="dashboard-container">
        <div className="user-info">
          <div className="flex items-center justify-between">
            <span>Welcome, {user?.username}</span>
            <button
              onClick={handleChangePassword}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-full shadow-sm transition absolute right-8 top-4"
            >
              <Key className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-6 mt-4">
            <button
              onClick={() => navigate('/dashboard', { state: { user } })}
              className={`text-lg font-medium transition-colors ${
                activeTab === 'tiles' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Tiles
            </button>
            <button
              onClick={() => setActiveTab('fund')}
              className={`text-lg font-medium transition-colors ${
                activeTab === 'fund' 
                  ? 'text-violet-400 border-b-2 border-violet-400' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Fund
            </button>
          </div>
        </div>

        <div className="fund-content mt-8 px-6">
          {!schwabAuthenticated ? (
            <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Connect to Charles Schwab</h2>
              <p className="text-gray-300 mb-6">
                To view your portfolio positions, you need to connect your Schwab account.
              </p>
              <button
                onClick={handleConnectSchwab}
                className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-lg mx-auto transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                Connect Schwab Account
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">Portfolio Positions</h1>
                  {lastUpdated && (
                    <p className="text-gray-400 text-sm mt-1">
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded"
                    />
                    Auto-refresh (7s)
                  </label>
                  <button
                    onClick={handleManualRefresh}
                    disabled={loading}
                    className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
              
              <PositionsTable positions={positions} loading={loading} />
            </div>
          )}
        </div>

        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={handleCloseChangePasswordModal}
          onChangePassword={handlePasswordChange}
          isChanging={changingPassword}
        />
      </div>
    </>
  );
}

export default FundPage;